import { lazy } from "react";
import Login from "./pages/Login/Login.js";
import Launchpad from "./pages/Launchpad/Launchpad.js";
import PageDefault from "./common/PageDefault/PageDefault.js";
import { RecoilRoot } from "recoil";
import { ReactFlowProvider } from "@xyflow/react";
import CategoryStats from "./pages/CategoryStats/CategoryStats.js"
import Data from "./scripts/DataManagement/Data.js";
import useAuthRedirect from "./useAuthRedirect.js";
import TaskResults from "./pages/TaskResults/TaskResults.js";
import TaskDetail from "./pages/TaskDetail/TaskDetail.js";
import Layout from "./Layout.js";
import UserProfile from "./pages/Admin/UserProfile.js";
import { AuthLayout } from "./AuthLayout.js";
import { matchPath } from "react-router";
import NotFound from "./NotFound.js";
import Error from "./Error.js";


// Admin Views
const AdminHub = lazy(() => import("./pages/Admin/AdminHub"));
const ManageTeams = lazy(() => import("./pages/Admin/ManageTeams"));
const ManageCategories = lazy(() => import("./pages/Admin/ManageCategories"));
const ManageSecurityTags = lazy(() => import("./pages/Admin/ManageSecurityTags"));
const ManageUsers = lazy(() => import("./pages/Admin/ManageUsers"));
const ManageRoles = lazy(() => import("./pages/Admin/ManageRoles"));
const SuperAdmin = lazy(() => import("./pages/Admin/SuperAdmin"));

const CreateWorkflow = lazy(() => import("./scripts/WorkflowDesigner/CreateWorkflow"));
const TaskHistory = lazy(() => import("./pages/TaskHistory/TaskHistory"));
// const TaskDetail = lazy(() => import("./pages/TaskDetail/TaskDetail"));
const Workflows = lazy(() => import("./scripts/WorkflowDesigner/Workflows"));
const WorkflowDesigner = lazy(() => import("./scripts/WorkflowDesigner/WorkflowDesigner"));
const WorkflowStats = lazy(() => import("./pages/WorkflowStats/WorkflowStats.js"));
const PasswordReset = lazy(() => import("./pages/Login/PasswordReset.js"));
const IssueDefectHubElements = lazy(() => import("./scripts/IssueDefectHubElements"));
const IssueDefectGrid = lazy(() => import("./scripts/IssueDefectGrid"));
const IssueDefectDetail = lazy(() => import("./scripts/IssueDefectDetail"));
const IssueDefectTypes = lazy(() => import("./scripts/IssueDefectTypes.js"));
const UserIssueDefectTypes = lazy(() => import("./scripts/UserIssueDefectTypes"));
const IssueAssignments = lazy(() => import("./scripts/issue-assignments"));
const UserStatsMui = lazy(() => import("./pages/UserStats/UserStats.js"));
const PasswordChangeAction = lazy(() => import("./pages/Login/PasswordChangeAction.js"));
const Tasks = lazy(() => import("./scripts/TaskExecution/Tasks"));
const TaskExecution = lazy(() => import("./scripts/TaskExecution/TaskExecution"));

function AuthComponent() {
	useAuthRedirect();
	return <> </>;
}

export const routes = [
	{ path: "/", Component: AuthComponent },
	{ path: "/login", Component: Login },
	{ path: "/reset-password", Component: PasswordReset },
	{ path: "/password-change-action", Component: PasswordChangeAction },
	{ path: "/404", Component: NotFound },
	{
		Component: Layout,
		children: [
			{
				path: "main",
				Component: PageDefault,
				ErrorBoundary: Error,
				children: [
					{
						path: "launchpad",
						children: [
							{ index: true, Component: Launchpad },
							{
								element: <AuthLayout role="viewer" />,
								children: [
									{ path: "task-history", Component: TaskHistory }
								]
							},
							{
								element: <AuthLayout role="manager" />,
								children: [
									{ path: "workflow-stats", Component: WorkflowStats },
									{ path: "user-stats", Component: UserStatsMui },
									{ path: "category-stats/:categoryID?/:tsstartDate?/:tsendDate?", Component: CategoryStats },
									{ path: "task-results/:workflowID/:originFrom/:tsstartDate?/:tsendDate?/:subjectID?", Component: TaskResults },
									{ path: "task-detail/:workflowID/:taskID", Component: TaskDetail,  handle: { hideFooter: true } }
								]
							},
							{
								path: "issues-defects",
								element: <AuthLayout role="viewer" />,
								children: [
									{ path: "IssueDefectHub", Component: IssueDefectHubElements },
									{ path: "IssueDefectTypes", Component: IssueDefectTypes },
									{ path: "UserIssueDefectTypes", Component: UserIssueDefectTypes },
									{ path: "issuetype/:id/:name/issues/:user?", Component: IssueDefectGrid },
									{ path: "assignee/:assigneeId/:assigneeName/issues", Component: IssueDefectGrid },
									{ path: "IssueDefectDetail/:id/:typeId/:user?", Component: IssueDefectDetail },
									{ path: "assignee/:assigneeId/:assigneeName/:id/:typeId/issue", Component: IssueDefectDetail },
									{ path: "IssueAssignments", Component: IssueAssignments }
								]
							},
							{
								element: <AuthLayout role="executor" />,
								children: [
									{ path: "TaskExecutionApp", Component: Tasks },
									{
										path: "TaskExecution/:id",
										element: (
											<RecoilRoot>
												<Data />
												<TaskExecution />
											</RecoilRoot>
										)
									}
								]
							},
							{
								element: <AuthLayout role="admin" />,
								children: [
									{ path: "AdminHub", Component: AdminHub },
									{ path: "ManageSecurityTags", Component: ManageSecurityTags },
									{ path: "ManageTeams", Component: ManageTeams },
									{ path: "ManageCategories", Component: ManageCategories },
									{ path: "ManageUsers", Component: ManageUsers },
									{ path: "ManageRoles", Component: ManageRoles },
									{ path: "UserProfile", Component: UserProfile }
								]
							},
							{
								element: <AuthLayout role="superuser" />,
								children: [
									{ path: "SuperAdmin", Component: SuperAdmin }
								]
							},
							{
								element: <AuthLayout role="designer" />,
								children: [
									{ path: "WorkflowApp", Component: Workflows },
									{
										path: "WorkflowDesigner/:id",
										element: (
											<RecoilRoot>
												<ReactFlowProvider>
													<Data />
													<WorkflowDesigner />
												</ReactFlowProvider>
											</RecoilRoot>
										)
									},
									{ path: "CreateWorkflow", Component: CreateWorkflow }
								]
							}
						]
					}
				]
			}
		]
	}
];

export const findRouteMatch = (routes, routePath, root = '', role = null) => {
	for (const route of routes) {
		const path = route.path ? root + '/' + route.path : '';
		if (route.path) {
			const match = matchPath({ path: path, end: true }, routePath);
			if (match) return { route, match, role };
		}
		if (route.children) {
			for (const child of route.children) {
				const childMatch = findRouteMatch(
					route.children,
					routePath,
					route.path ? path : root,
					route.element?.props?.role || role);
				if (childMatch) return { child, childMatch, role: childMatch.role };
			}
		}
	}
	return null;
};
